import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs/operators';
import { objectContainsTerm } from 'src/app/shared/utils/object-search.util';

import { Comment } from 'src/app/models/comment.model';
import {
  CSVTicket,
  Ticket,
  TicketGrouping,
  TicketGroupingLabel,
  TicketRecordType,
  TicketStatus,
  TicketType,
} from 'src/app/models/ticket/ticket.model';
import {
  getTicketType,
  mapStringToTicketGrouping,
} from 'src/app/models/ticket/ticket.util';
import * as fromStore from 'src/app/store/';

@Component({
  selector: 'app-ticket-list-display',
  templateUrl: './ticket-list-display.component.html',
  styleUrls: ['./ticket-list-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TicketListDisplayComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() browserSize: string;

  @Input() tickets: Ticket[] = [];

  @Input() totalTickets: number;

  @Input() csvData: CSVTicket[];

  @Input() pageSize: number;

  @Input() currentPage: number;

  @Input() isLoading: boolean;

  @Output() selectTicket = new EventEmitter<Ticket>();

  @Output() openCreateTicketDialog = new EventEmitter();

  @Output() paginationChanged = new EventEmitter<{
    page: number;
    pageSize: number;
  }>();

  @Output() searchChanged = new EventEmitter<string>();

  @ViewChild('headerContainer', { static: false }) headerContainer: ElementRef;

  validStatusValues = [TicketStatus.Open, TicketStatus.Closed];

  headerHeight: number;

  searchTermControl = new UntypedFormControl();

  selectedPanel = '';

  isOrderingAsc = true;

  currentGrouping: TicketGrouping = TicketGrouping.Type;

  TicketGrouping = TicketGrouping;

  previousGrouping: TicketGrouping = null;

  groupedTickets: { key: string | TicketRecordType; tickets: Ticket[] }[] = [];

  validGroupByValues: Record<string, string> = {
    Location: TicketGroupingLabel.Location,
    Status: TicketGroupingLabel.Status,
    Type: TicketGroupingLabel.Type,
  };

  validTypeValues: string[] = Object.keys(TicketType);

  selectedTicketId: string = null;

  // TODO: remove when Redis is ready
  searchTerm = '';

  filteredTicketsByParams: Ticket[] = [];

  filteredTickets: Ticket[] = [];

  filteredTotal = 0;

  private activatedRoute = inject(ActivatedRoute);

  private route = inject(Router);

  private cdr = inject(ChangeDetectorRef);

  private store = inject(Store<fromStore.State>);

  private destroy$ = new Subject<void>();

  private selectedTicket$ = this.store.select<Ticket>(
    fromStore.getSelectedTicket,
  );

  ngOnInit(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        // If no groupBy parameter is provided, navigate with default
        if (!params.groupBy) {
          this.route.navigate([], {
            queryParams: { groupBy: TicketGroupingLabel.Type },
            replaceUrl: true, // Replace current URL to avoid back button issues
          });
          return;
        }
        this.applyFilters(params);
        this.cdr.detectChanges();
      });

    // TODO: enable search filtering when Redis is ready
    // this.searchTermControl.valueChanges
    //   .pipe(debounceTime(500), takeUntil(this.destroy$))
    //   .subscribe((value: string) => {
    //     this.searchChanged.emit(value);
    //   });

    this.searchTermControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value: string) => {
        this.searchTerm = (value ?? '').trim();
        this.applySearch();
      });

    this.store
      .select(fromStore.getSelectedTicket)
      .pipe(
        takeUntil(this.destroy$),
        map((t) => t?.Id ?? null),
        distinctUntilChanged(),
      )
      .subscribe((id) => {
        this.selectedTicketId = id;
        this.cdr.detectChanges();
      });
  }

  ngOnChanges(): void {
    this.applyFilters(this.activatedRoute.snapshot.queryParams);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyFilters(params: {
    groupBy?: TicketGroupingLabel;
    status?: TicketStatus;
    type?: TicketRecordType;
    olderThan?: string;
    last?: string;
  }): void {
    if (!this.tickets?.length) {
      this.groupedTickets = [];
      this.filteredTicketsByParams = [];
      this.filteredTickets = [];
      this.filteredTotal = 0;
      this.cdr.detectChanges();
      return;
    }

    this.isLoading = true;
    this.groupedTickets = [];

    // Default to 'Type' if no groupBy parameter is provided
    const groupByParam = params.groupBy || TicketGroupingLabel.Type;
    const groupBy = TicketGroupingLabel[groupByParam];
    if (!groupBy?.length) {
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.currentGrouping = mapStringToTicketGrouping(groupBy);

    let filtered = [...this.tickets];

    if (
      TicketGroupingLabel[groupBy] !== undefined &&
      this.validStatusValues.includes(params.status)
    ) {
      if (TicketGroupingLabel[groupBy] === TicketGroupingLabel.Status) {
        if (this.validTypeValues.includes(params.type)) {
          filtered = filtered.filter((t) =>
            getTicketType(t).includes(params.type),
          );
        }
      } else if (TicketGroupingLabel[groupBy] === TicketGroupingLabel.Type) {
        filtered = filtered.filter((t) =>
          getTicketType(t).includes(params.type),
        );
      }
    }

    if (params.olderThan) {
      const olderthan = parseInt(params.olderThan, 10);
      filtered = filtered.filter(
        (t) => moment().diff(t.CreatedDate, 'days') > olderthan,
      );
    }

    if (params.last) {
      const last = parseInt(params.last, 10);
      filtered = filtered.filter(
        (t) => moment().diff(t.CreatedDate, 'days') <= last,
      );
    }

    // Save param-filtered baseline
    this.filteredTicketsByParams = filtered;

    // 3) Now apply the text search + regroup
    this.applySearch();
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  applyFiltersREDIS(params: {
    groupBy?: TicketGroupingLabel;
    status?: TicketStatus;
    type?: TicketRecordType;
    olderThan?: string;
    last?: string;
  }): void {
    if (this.tickets?.length === 0) {
      this.groupedTickets = [];
      this.cdr.detectChanges();
    } else if (this.tickets.length) {
      this.isLoading = true;
      this.groupedTickets = [];
      const groupBy = TicketGroupingLabel[params.groupBy];
      if (!groupBy?.length) return;

      this.currentGrouping = mapStringToTicketGrouping(groupBy);

      let filteredTickets = this.tickets;

      if (
        TicketGroupingLabel[groupBy] !== undefined &&
        this.validStatusValues.includes(params.status)
      ) {
        if (TicketGroupingLabel[groupBy] === TicketGroupingLabel.Status) {
          if (this.validTypeValues.includes(params.type)) {
            if (filteredTickets?.length > 0) {
              filteredTickets = filteredTickets.filter((t) => {
                const ticketTypes = getTicketType(t);
                return ticketTypes.includes(params.type);
              });
            }
          }
        } else if (TicketGroupingLabel[groupBy] === TicketGroupingLabel.Type) {
          if (filteredTickets.length > 0) {
            filteredTickets = filteredTickets.filter((t) => {
              const ticketTypes = getTicketType(t);
              return ticketTypes.includes(params.type);
            });
          }
        }
      }

      if (params.olderThan) {
        const olderthan = parseInt(params.olderThan, 10);
        if (filteredTickets.length > 0) {
          filteredTickets = filteredTickets.filter((t) => {
            const currentDate = moment();
            const differenceInDays = currentDate.diff(t.CreatedDate, 'days');
            return differenceInDays > olderthan;
          });
        }
      }

      if (params.last) {
        const last = parseInt(params.last, 10);
        if (filteredTickets.length > 0) {
          filteredTickets = filteredTickets.filter((t) => {
            const currentDate = moment();
            const differenceInDays = currentDate.diff(t.CreatedDate, 'days');
            return differenceInDays <= last;
          });
        }
      }

      this.groupTickets(this.currentGrouping, filteredTickets);
      if (this.groupedTickets.length > 0) {
        this.onSelectTicket(this.groupedTickets[0].tickets[0]);
      } else {
        this.onSelectTicket(null);
      }
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  onExpanded(): void {
    const serviceDiv = document.getElementById(this.selectedPanel);
    serviceDiv.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }

  onHeaderClick(e: MouseEvent): void {
    this.selectedPanel = (e.currentTarget as HTMLElement).id;
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.paginationChanged.emit({
      page: event.pageIndex,
      pageSize: event.pageSize,
    });
    this.store.dispatch(new fromStore.SetSelectedTicket(null));
  }

  openDialog(): void {
    this.openCreateTicketDialog.emit();
  }

  onSelectTicket($event: Ticket): void {
    if (!$event?.Id) {
      this.store.dispatch(new fromStore.SetSelectedTicket(null));
      this.selectedTicketId = null;
      this.selectTicket.emit(null);
      this.cdr.detectChanges();
      return;
    }
    this.selectedTicketId = $event?.Id;
    this.store.dispatch(new fromStore.SetSelectedTicket($event?.Id));
    this.selectTicket.emit($event);
    this.cdr.detectChanges();
  }

  hasUnreadComments = (comments: Comment[]): boolean => {
    if (comments.length) {
      return comments.some((c) => !c.Is_Read__c);
    }
    return false;
  };

  onClickGroupBy(value: string): void {
    const grouping = mapStringToTicketGrouping(value);
    if (this.currentGrouping === grouping) {
      this.isOrderingAsc = !this.isOrderingAsc;
      this.sortTicketGroups();
    } else {
      this.currentGrouping = grouping;
      this.route.navigate([], { queryParams: { groupBy: value } });
      this.groupTickets(this.currentGrouping, this.tickets);
      if (!this.isOrderingAsc) {
        this.sortTicketGroups();
      }
    }
  }

  groupTickets(grouping: TicketGrouping, currTickets?: Ticket[]): void {
    this.previousGrouping = this.currentGrouping;

    switch (grouping) {
      case TicketGrouping.Type:
        this.groupTicketsByType(currTickets);
        break;
      case TicketGrouping.Status:
        this.groupTicketsByStatus(currTickets);
        break;
      case TicketGrouping.Location:
        this.groupTicketsByLocation(currTickets);
        break;
      default:
        // In case new groupings are added but not handled
        this.groupTicketsByType(currTickets);
        break;
    }
  }

  groupTicketsByType(tickets: Ticket[]): void {
    this.groupedTickets = [];
    const careTickets = {
      key: TicketRecordType.Care,
      tickets: tickets.filter(
        (ticket) => getTicketType(ticket)[0] === TicketRecordType.Care,
      ),
    };
    const financeTickets = {
      key: TicketRecordType.Finance,
      tickets: tickets.filter(
        (ticket) => getTicketType(ticket)[0] === TicketRecordType.Finance,
      ),
    };
    const maintenanceTickets = {
      key: TicketRecordType.Maintenance,
      tickets: tickets.filter(
        (ticket) => getTicketType(ticket)[0] === TicketRecordType.Maintenance,
      ),
    };
    const proactiveTickets = {
      key: TicketRecordType.Proactive,
      tickets: tickets.filter(
        (ticket) => getTicketType(ticket)[0] === TicketRecordType.Proactive,
      ),
    };
    const supportTickets = {
      key: TicketRecordType.Support,
      tickets: tickets.filter(
        (ticket) => getTicketType(ticket)[0] === TicketRecordType.Support,
      ),
    };

    if (careTickets.tickets.length > 0) {
      this.groupedTickets.push(careTickets);
    }
    if (financeTickets.tickets.length > 0) {
      this.groupedTickets.push(financeTickets);
    }
    if (maintenanceTickets.tickets.length > 0) {
      this.groupedTickets.push(maintenanceTickets);
    }
    if (proactiveTickets.tickets.length > 0) {
      this.groupedTickets.push(proactiveTickets);
    }
    if (supportTickets.tickets.length > 0) {
      this.groupedTickets.push(supportTickets);
    }
  }

  groupTicketsByStatus(tickets: Ticket[]): void {
    this.groupedTickets = [];
    const allStatuses = new Map();
    allStatuses.set(TicketStatus.Open, { key: TicketStatus.Open, tickets: [] });
    allStatuses.set(TicketStatus.Closed, {
      key: TicketStatus.Closed,
      tickets: [],
    });

    tickets.forEach((ticket) => {
      const groupName = ticket.IsClosed
        ? TicketStatus.Closed
        : TicketStatus.Open;
      allStatuses.get(groupName).tickets.push(ticket);
    });
    if (allStatuses.get(TicketStatus.Open).tickets.length > 0) {
      this.groupedTickets.push(allStatuses.get(TicketStatus.Open));
    }
    if (allStatuses.get(TicketStatus.Closed).tickets.length > 0) {
      this.groupedTickets.push(allStatuses.get(TicketStatus.Closed));
    }
  }

  groupTicketsByLocation(tickets: Ticket[]): void {
    this.groupedTickets = [];
    // get all locations that there could be
    const locations = new Set(
      this.tickets.map((ticket) => ticket.Service_Location_Address__c),
    );
    // loop through all the addresses, and create a group for them
    locations.forEach((location) => {
      const locationGroup = {
        key: location,
        tickets: tickets.filter(
          (ticket) => ticket.Service_Location_Address__c === location,
        ),
      };
      if (locationGroup.tickets.length > 0) {
        this.groupedTickets.push(locationGroup);
      }
    });

    this.groupedTickets = this.groupedTickets.sort((statusA, statusB) => {
      if (statusA.key < statusB.key) {
        return -1;
      }
      if (statusA.key > statusB.key) {
        return 1;
      }
      return 0;
    });
  }

  sortTicketGroups(): void {
    this.groupedTickets.reverse();
  }

  shouldExpand(
    ticketGroup: { key: any; tickets: Ticket[] },
    isFirst: boolean,
  ): boolean {
    const { status } = this.activatedRoute.snapshot.queryParams;
    const { type } = this.activatedRoute.snapshot.queryParams;

    if (typeof status === 'string' || typeof type === 'string') {
      return ticketGroup.key === status || ticketGroup.key === type;
    }

    let shouldExpand = false;
    this.selectedTicket$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedTicket) => {
        if (selectedTicket?.Id) {
          const ticketStatus = selectedTicket.IsClosed
            ? TicketStatus.Closed
            : TicketStatus.Open;
          const ticketTypes = getTicketType(selectedTicket);
          shouldExpand =
            ticketGroup.key === ticketStatus ||
            ticketTypes.includes(ticketGroup.key as TicketRecordType);
        }
      });

    return shouldExpand || isFirst;
  }

  private applySearch(): void {
    const searchTerm = (this.searchTerm || '').trim().toLowerCase();
    const isStatusOpen = searchTerm === 'open';
    const isStatusClosed = searchTerm === 'closed';
    const fields = [
      'CaseNumber',
      'Service_Location_Address__c',
      'Service__r.Name',
      'Service__r.Bandwidth_Upstream__c',
      'Service__r.RecordType.Name',
      'Service__r.Vendor__r.Name',
      'Subject',
      'Comments__r.records.Body__c',
    ];

    this.filteredTickets = (() => {
      if (!searchTerm) return [...this.filteredTicketsByParams];
      if (isStatusOpen)
        return this.filteredTicketsByParams.filter(
          (ticket) => !ticket.IsClosed,
        );
      if (isStatusClosed)
        return this.filteredTicketsByParams.filter((ticket) => ticket.IsClosed);
      return this.filteredTicketsByParams.filter((ticket) =>
        objectContainsTerm(ticket, searchTerm, fields),
      );
    })();

    this.groupTickets(this.currentGrouping, this.filteredTickets);
    this.filteredTotal = this.filteredTickets.length;

    if (this.groupedTickets.length > 0) {
      this.onSelectTicket(this.groupedTickets[0].tickets[0]);
    } else {
      this.onSelectTicket(null);
    }

    this.cdr.markForCheck();
  }
}
